<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLoadingBar } from 'naive-ui';
import ContractViewGrid, { type ColumnDef } from '../components/ContractViewGrid.vue';
import type { TreeNode } from 'primevue/treenode';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import {
  type ContractManagementViewInDb,
  type ContractManagementExtractionInDb,
  type ContractManagementContractFileInDb,
  type ContractManagementContractFileType,
  type ContractManagementViewUpdate,
  type AndCriterionInput,
  type AndCriterionOutput,
  type ContractManagementMatterInDb,
  type EnumResponseType,
  type ContractManagementViewTable,
  TaskStatus,
} from '@/api';
import CreateColumnDrawer from '@/components/contractManagement/CreateColumnDrawer.vue';
import UpdateColumnDrawer from '@/components/contractManagement/UpdateColumnDrawer.vue';
import ViewColumnPopover from '@/components/contractManagement/ViewColumnPopover.vue';
import GaiaViewSourcesModal from '@/components/design/domain/contractManagement/GaiaViewSourcesModal.vue';
import ContractManagementExtractionDetailsModal from '@/components/contractManagement/ContractManagementExtractionDetailsModal.vue';
import EditStaticColumnModal from '@/components/contractManagement/EditStaticColumnModal.vue';
import EditStaticColumnDrawer from '@/components/contractManagement/EditStaticColumnDrawer.vue';
// FilterBar.vue was replaced on this branch by GaiaFilterPanel, which now owns
// the FilterConfig type. Only the type is needed here — the strip below is
// built from gaia-filter-bar and chips rather than the panel.
import { type FilterConfig } from '@/components/design/composite/GaiaFilterPanel.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterBar from '@/components/design/composite/GaiaFilterBar.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import Layout from '@/components/design/layout/Layout.vue';
import { gaiaNeutral, gaiaShellPadding } from '@/components/design/themes/tokens';
import VaultFileRevisionUploadModal from '@/components/file_vault/VaultFileRevisionUploadModal.vue';
import {
  type StaticColumnType,
  type StaticColumnInfo,
  type ConfigurableStaticColumnType,
  STATIC_COLUMN_CONFIGS,
  isEntityBasedStaticColumn,
  staticColumnSortKey,
  type EntityBasedStaticColumnType,
  type ViewColumnOrderItem,
} from '@/types/contractManagement/viewTypes';
import { normalizeFilterFromApi } from '@/utils/contractManagementView/staticFilters';
import { getElementType } from '@/utils/contractManagementView/filterValueValidation';
import { formatDateInputDisplay, utcDayFromValue } from '@/utils/dateInput';
import {
  CONTRACT_ICON_STATES,
  contractStateLabel,
} from '@/components/design/domain/contractManagement/contractStateIcons';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { logger } from '@/utils/logger';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import { safeStringify } from '@/utils/jsonHelpers';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useAuthStore } from '@/stores/auth';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { contractRoute, contractFileRoute } from '@/router/links';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';

// --- Router & State ---
const route = useRoute();
const router = useRouter();
const loadingBar = useLoadingBar();
const message = useGaiaToast();

// Get the view ID from URL
const currentViewId = computed(() => route.params.viewId as string);

// --- Local State ---
const isLoading = ref(false);
const isRefreshingTable = ref(false); // Separate state for table refresh (shows overlay)
const isTableFetching = ref(false); // Tracks whether a table API call is in flight (prevents auto-refresh pileup)
const error = ref<string | null>(null);
const viewData = ref<ContractManagementViewInDb | null>(null);
const searchQuery = ref('');
const treeNodeData = ref<TreeNode[]>([]);
const previousTreeNodeData = ref<TreeNode[]>([]); // Store previous data during refresh
const columnDefs = ref<ColumnDef[]>([]);
const rawTableData = ref<ContractManagementViewTable | null>(null);
const autoRefreshIntervalSeconds = ref<number>(15);
const latestTableFetchId = ref(0);
const overlayTableFetchId = ref<number | null>(null);
let tableFetchCounter = 0;
let autoRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
let consecutiveAutoRefreshErrors = 0;
const MAX_AUTO_REFRESH_ERRORS = 3;

// Helper function to reset refresh state
const resetRefreshState = (): void => {
  isRefreshingTable.value = false;
  previousTreeNodeData.value = [];
};

// Ensure refresh state is always properly initialized
watch(
  () => viewData.value,
  () => {
    // Reset refresh state when view data changes
    if (!viewData.value) {
      resetRefreshState();
    }
  }
);
const showCreateColumnDrawer = ref(false);
const showUpdateColumnDrawer = ref(false);
const showColumnsPopover = ref(false);
const showSourcesModal = ref(false);

const authStore = useAuthStore();
const contractTypeStore = useContractTypeStore();
/** Only the view's owner and org / platform admins may change what it covers. */
const canEditSources = computed(() => {
  const { is_org_admin, is_platform_admin } = authStore.userPermissions;
  if (is_org_admin || is_platform_admin) return true;
  const ownerUid = viewData.value?.owner_user?.uid;
  return ownerUid !== undefined && ownerUid === authStore.backendUser?.uid;
});

const editingColumnUid = ref<string | null>(null);

// Static column edit drawer state
const showEditStaticColumnDrawer = ref(false);
const editingStaticColumnType = ref<StaticColumnType | null>(null);

// Pagination state
const currentPage = ref<number>(1);
const pageSize = ref<number>(50);
const totalCount = ref<number>(0);

// Filter state
const filterOptions = ref<Record<string, (string | null)[]>>({});
const { columnFilters, setColumnFilters, sortKey, sortDirection, sortActivated } =
  useListQuerySync();
// Column filters live in the URL (?f_<key>=…); mirror local filter state to it.
const activeFilters = ref<Record<string, unknown>>({ ...columnFilters.value });
watch(
  activeFilters,
  () => {
    setColumnFilters(activeFilters.value);
  },
  { deep: true }
);
const showFilterBar = ref<boolean>(false);

const buildEnumResponseType = (
  values: (string | null)[] | undefined,
  multipleChoices = false
): EnumResponseType | undefined => {
  if (!values) return undefined;

  const normalized = values.map(v => (v ?? '').trim()).filter(v => v.length > 0);

  if (normalized.length === 0) return undefined;

  const unique = Array.from(new Set(normalized));

  return {
    multiple_choices: multipleChoices,
    values: unique.map(name => ({
      name,
      definition: '',
    })),
  };
};

// Build enum response type for contract type from backend filter options, falling back
// to the organisation's contract types
const typeEnumResponseType = computed((): EnumResponseType | undefined => {
  const fromFilters = buildEnumResponseType(filterOptions.value['type']);
  if (fromFilters) return fromFilters;

  return {
    multiple_choices: false,
    values: contractTypeStore.sortedContractTypes.map(type => ({
      name: type.name,
      definition: '',
      color: null,
    })),
  };
});

const tagsEnumResponseType = computed(() =>
  buildEnumResponseType(filterOptions.value['tags'], true)
);

// Contract files cache for lazy loading
const contractFilesCache = ref<
  Map<
    string,
    {
      files: [
        ContractManagementContractFileInDb,
        (
          | ContractManagementExtractionInDb
          | ContractualParty[]
          | ContractManagementMatterInDb[]
          | string // for created_at and type (document_type)
          | string[] // for tags (contract_tags)
          | ContractManagementContractFileType
          | null
        )[],
      ][];
      total: number;
    }
  >
>(new Map());
const loadingContractFiles = ref<Set<string>>(new Set());
// Persistent cache for entity names (contractual_party, matter, owner, effective_from, effective_to)
// This accumulates across all data loads to ensure filter labels are always available
const entityNameCache = ref<Record<EntityBasedStaticColumnType, Map<string, string>>>({
  contractual_party: new Map(),
  matter: new Map(),
  owner: new Map(),
  effective_from: new Map(),
  effective_to: new Map(),
});

// Edit modal state
const showEditModal = ref(false);
const editModalCellData = ref<{
  columnUid: string;
  extraction: ContractManagementExtractionInDb | null;
  column: ContractManagementViewInDb['columns'][number] | null;
  contractUid?: string;
  fileUid?: string;
}>({
  columnUid: '',
  extraction: null,
  column: null,
});

// Static column edit modal state
const showStaticColumnEditModal = ref(false);
const staticColumnEditData = ref<{
  staticColumnType: StaticColumnType | null;
  entities: ContractualParty[];
  contractFileUid: string | null;
  entitiesWithFiles?: {
    entity: ContractualParty;
    contractFileUid: string;
    contractFileName?: string;
  }[];
} | null>(null);

const showRevisionUploadModal = ref(false);
const revisionUploadFileUid = ref('');
const revisionUploadFileName = ref('');

// --- Computed Properties ---
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
const viewTitle = computed(() => viewData.value?.name || 'Detail View');

/** "143 contracts" — what this grid is a window onto. */
const viewSubtitle = computed(() =>
  !isLoading.value && !error.value
    ? `${totalCount.value} contract${totalCount.value === 1 ? '' : 's'}`
    : ''
);

// Computed property to determine which data to show in the table
const tableRowData = computed(() => {
  // If refreshing and we have previous data, show previous data
  if (isRefreshingTable.value && previousTreeNodeData.value.length > 0) {
    return previousTreeNodeData.value;
  }
  // Otherwise, always show current data (even if empty - table will handle it)
  return treeNodeData.value;
});

/** Next order_index for a new column: max(existing order_indices) + 1, so new columns are always appended. */
const currentColumnCount = computed(() => {
  const view = viewData.value;
  if (!view) return 0;
  const indices: number[] = [];
  for (const config of Object.values(STATIC_COLUMN_CONFIGS) as {
    nameKey: keyof ContractManagementViewInDb;
    visibleKey: keyof ContractManagementViewInDb;
    orderIndexKey: keyof ContractManagementViewInDb;
    fieldKey: string;
  }[]) {
    const v = view[config.orderIndexKey];
    if (typeof v === 'number') indices.push(v);
  }
  for (const col of view.columns) {
    if (typeof col.order_index === 'number') indices.push(col.order_index);
  }
  return indices.length > 0 ? Math.max(...indices) + 1 : 0;
});

// Memoize sorted columns to avoid repeated calculations
const sortedColumns = computed(() => {
  if (!viewData.value) return null;
  return getAllVisibleColumnsSorted(viewData.value);
});

const viewComplete = computed(() => {
  if (!viewData.value || !rawTableData.value) return false;

  const { allColumns } = getAllVisibleColumnsSorted(viewData.value);
  const hasDynamicColumns = allColumns.some(col => !('fieldKey' in col));
  if (!hasDynamicColumns) return true;

  for (const [, extractions] of rawTableData.value.results) {
    for (const [colIndex, column] of allColumns.entries()) {
      if ('fieldKey' in column) continue;

      const extractionValue = extractions[colIndex] as ContractManagementExtractionInDb | null;
      if (!extractionValue || typeof extractionValue !== 'object') {
        return false;
      }

      if (
        extractionValue.processing_state === TaskStatus.PENDING ||
        extractionValue.processing_state === TaskStatus.RUNNING
      ) {
        // Consider complete if user applied human override (stop polling; same as ExtractionCellState)
        const hasHumanOverride =
          extractionValue.human_overwrite != null && extractionValue.human_overwrite.value != null;
        if (!hasHumanOverride) {
          return false;
        }
        // Has override: treat as complete, continue to next cell
      } else if (
        extractionValue.processing_state !== TaskStatus.DONE &&
        extractionValue.processing_state !== TaskStatus.FAILED
      ) {
        return false;
      }
    }
  }

  return true;
});

// --- Helper Functions ---
const cloneTableData = <T,>(data: T): T => {
  try {
    return structuredClone(data);
  } catch {
    return JSON.parse(JSON.stringify(data)) as T;
  }
};

const stopAutoRefresh = (): void => {
  if (autoRefreshIntervalId) {
    clearInterval(autoRefreshIntervalId);
    autoRefreshIntervalId = null;
  }
};

const startAutoRefresh = (): void => {
  stopAutoRefresh(); // Always clear existing before starting new
  const intervalMs = autoRefreshIntervalSeconds.value * 1000;
  if (intervalMs <= 0 || viewComplete.value) return;

  consecutiveAutoRefreshErrors = 0;
  autoRefreshIntervalId = setInterval(async () => {
    if (!currentViewId.value || !viewData.value) return;
    if (viewComplete.value) {
      stopAutoRefresh();
      return;
    }
    void loadFilterOptions({ isAutoRefresh: true });
    try {
      await loadTableData({ showOverlay: false, preserveExpandedState: true, isAutoRefresh: true });
      consecutiveAutoRefreshErrors = 0;
    } catch {
      consecutiveAutoRefreshErrors += 1;
      if (consecutiveAutoRefreshErrors >= MAX_AUTO_REFRESH_ERRORS) {
        stopAutoRefresh();
      }
    }
  }, intervalMs);
};

/**
 * Get static columns from view data dynamically
 * Extracts all static columns based on the STATIC_COLUMN_CONFIGS configuration
 */
const getStaticColumns = (view: ContractManagementViewInDb): StaticColumnInfo[] => {
  const staticColumns: StaticColumnInfo[] = [];

  for (const [type, config] of Object.entries(STATIC_COLUMN_CONFIGS) as [
    ConfigurableStaticColumnType,
    {
      nameKey: keyof ContractManagementViewInDb;
      visibleKey: keyof ContractManagementViewInDb;
      orderIndexKey: keyof ContractManagementViewInDb;
      fieldKey: string;
    },
  ][]) {
    const name = view[config.nameKey];
    const visible = view[config.visibleKey];
    const orderIndex = view[config.orderIndexKey];

    if (
      name &&
      typeof name === 'string' &&
      typeof visible === 'boolean' &&
      typeof orderIndex === 'number'
    ) {
      staticColumns.push({
        type,
        name,
        visible,
        order_index: orderIndex,
        fieldKey: config.fieldKey,
      });
    }
  }

  return staticColumns;
};

const getStaticColumnsForPopover = (view: ContractManagementViewInDb): StaticColumnInfo[] => {
  return [
    {
      type: 'name',
      name: 'Name',
      visible: true,
      order_index: -1,
      fieldKey: 'name',
    },
    ...getStaticColumns(view),
  ];
};

/**
 * Interface for column ordering
 * Used to normalize static and dynamic columns for sorting
 */
interface ColumnOrdering {
  order_index: number;
  uid?: string; // Dynamic columns have uid, static columns don't
}

/**
 * Helper function to extract ordering information from a column
 * Static columns don't have uid, dynamic columns do
 */
const getColumnOrdering = (
  col: StaticColumnInfo | ContractManagementViewInDb['columns'][number]
): ColumnOrdering => {
  if ('fieldKey' in col) {
    // Static column - no uid
    return {
      order_index: col.order_index,
    };
  } else {
    // Dynamic column - has uid
    return {
      order_index: col.order_index,
      uid: col.uid,
    };
  }
};

/**
 * Helper function to sort columns by order_index
 * When order_index is identical, static columns (no uid) come AFTER dynamic columns (has uid)
 */
const sortColumnsByOrderIndex = <
  T extends StaticColumnInfo | ContractManagementViewInDb['columns'][number],
>(
  columns: T[]
): T[] => {
  return [...columns].sort((a, b) => {
    const aOrdering = getColumnOrdering(a);
    const bOrdering = getColumnOrdering(b);

    // Primary sort: order_index
    if (aOrdering.order_index !== bOrdering.order_index) {
      return aOrdering.order_index - bOrdering.order_index;
    }

    // Secondary sort: when order_index is the same
    // Static columns (no uid) come AFTER dynamic columns (has uid)
    const aIsStatic = !aOrdering.uid;
    const bIsStatic = !bOrdering.uid;

    if (aIsStatic && !bIsStatic) return 1; // Static goes AFTER Dynamic
    if (!aIsStatic && bIsStatic) return -1; // Dynamic goes BEFORE Static

    return 0;
  });
};

/**
 * Get all visible columns (static + dynamic) sorted by order_index
 * Uses the helper function to ensure consistent sorting logic
 */
const getAllVisibleColumnsSorted = (
  view: ContractManagementViewInDb
): {
  staticColumns: StaticColumnInfo[];
  dynamicColumns: ContractManagementViewInDb['columns'];
  allColumns: (StaticColumnInfo | ContractManagementViewInDb['columns'][number])[];
} => {
  // Get static columns
  const allStaticColumns = getStaticColumns(view);
  const visibleStaticColumns = allStaticColumns.filter(col => col.visible);

  // Get dynamic columns
  const visibleDynamicColumns = view.columns.filter(col => col.visible);

  // Combine ALL columns (static + dynamic) and sort using helper function
  const allColumns: (StaticColumnInfo | ContractManagementViewInDb['columns'][number])[] =
    sortColumnsByOrderIndex([...visibleStaticColumns, ...visibleDynamicColumns]);

  // Also sort static and dynamic separately for other uses
  const sortedStaticColumns = sortColumnsByOrderIndex(visibleStaticColumns);
  const sortedDynamicColumns = sortColumnsByOrderIndex(visibleDynamicColumns);

  return {
    staticColumns: sortedStaticColumns,
    dynamicColumns: sortedDynamicColumns,
    allColumns,
  };
};

/**
 * Extracts display names from static column entity arrays
 * Entity static columns (contractual_party, matter, effective_from, effective_to) contain arrays of entities
 * This function reads each entity's label (its name, or its date) and joins them with commas
 *
 * @param entities Array of static column entity objects
 * @returns Comma-separated string of entity names, or null if empty/invalid
 */
const extractStaticColumnNames = (entities: unknown[] | null | undefined): string | null => {
  if (!entities || !Array.isArray(entities) || entities.length === 0) {
    return null;
  }

  const names = entities.map(entity => staticEntityName(entity)).filter(name => name.length > 0);

  return names.length > 0 ? names.join(', ') : null;
};

/**
 * Update entity name cache from static column data
 * This accumulates entity names across all data loads
 * Only applies to entity-based static columns (contractual_party, matter, owner)
 */
const updateEntityNameCache = (
  staticData: { uid: string }[] | null | undefined,
  type: StaticColumnType
): void => {
  if (!staticData || !Array.isArray(staticData)) return;

  // Only update cache for entity-based static columns
  if (!isEntityBasedStaticColumn(type)) return;

  const cache = entityNameCache.value[type];

  for (const entity of staticData) {
    const name = staticEntityName(entity);
    if (entity.uid && name) {
      cache.set(entity.uid, name);
    }
  }
};

/**
 * Transform API table response into TreeNode format for ContractViewGrid
 */
const transformTableDataToTreeNodes = (
  results: ContractManagementViewTable['results'],
  view: ContractManagementViewInDb
): TreeNode[] => {
  const treeData: TreeNode[] = [];

  // Get all visible columns (static + dynamic) sorted correctly
  const { allColumns } = getAllVisibleColumnsSorted(view);

  for (const [index, [contract, extractions]] of results.entries()) {
    const contractData: Record<string, unknown> = {
      __rowNumber: (currentPage.value - 1) * pageSize.value + index + 1,
      name: contract.name,
      uid: contract.uid,
      type: contract.contract_type?.name ?? '',
      description: contract.description,
      tags: contract.tags,
      created_at: contract.created_at,
      updated_at: contract.updated_at,
    };

    const staticColumnData: Record<
      string,
      unknown[] | string | string[] | Record<string, unknown> | null
    > = {};
    const dynamicExtractionsMap = new Map<string, ContractManagementExtractionInDb | null>();

    // Map extraction values using the corrected sort order
    for (const [colIndex, col] of allColumns.entries()) {
      // Safety check for bounds
      if (colIndex >= extractions.length) continue;

      const extractionValue = extractions[colIndex];

      if ('fieldKey' in col) {
        // Static column
        const staticCol = col;
        switch (staticCol.type) {
          case 'created_at':
          case 'type': {
            staticColumnData[staticCol.type] =
              typeof extractionValue === 'string' ? extractionValue : null;

            break;
          }
          case 'owner': {
            staticColumnData[staticCol.type] =
              extractionValue && typeof extractionValue === 'object' && 'uid' in extractionValue
                ? extractionValue
                : null;

            break;
          }
          case 'tags': {
            staticColumnData[staticCol.type] =
              Array.isArray(extractionValue) &&
              extractionValue.every(item => typeof item === 'string')
                ? extractionValue
                : null;

            break;
          }
          case 'state': {
            // `{contract_state, amendment_state}` on a contract row.
            staticColumnData[staticCol.type] =
              extractionValue &&
              typeof extractionValue === 'object' &&
              'contract_state' in extractionValue
                ? extractionValue
                : null;

            break;
          }
          default:
            if (extractionValue !== null && Array.isArray(extractionValue)) {
              staticColumnData[staticCol.type] = extractionValue;
            } else {
              staticColumnData[staticCol.type] = null;
            }
        }
      } else {
        // Dynamic column
        const dynamicCol = col;
        if (extractionValue && typeof extractionValue === 'object' && 'uid' in extractionValue) {
          dynamicExtractionsMap.set(
            dynamicCol.uid,
            extractionValue as ContractManagementExtractionInDb
          );
        } else {
          dynamicExtractionsMap.set(dynamicCol.uid, null);
        }
      }
    }

    // Populate data object based on maps created above
    for (const col of allColumns) {
      if ('fieldKey' in col) {
        const staticCol = col;
        switch (staticCol.type) {
          case 'created_at': {
            const createdAt =
              typeof staticColumnData[staticCol.type] === 'string'
                ? staticColumnData[staticCol.type]
                : contract.created_at;
            contractData[staticCol.fieldKey] = createdAt ?? null;

            break;
          }
          case 'type': {
            const documentType =
              typeof staticColumnData[staticCol.type] === 'string'
                ? staticColumnData[staticCol.type]
                : contract.contract_type?.name;
            contractData[staticCol.fieldKey] = documentType ?? null;

            break;
          }
          case 'tags': {
            const tags = Array.isArray(staticColumnData[staticCol.type])
              ? staticColumnData[staticCol.type]
              : contract.tags;
            contractData[staticCol.fieldKey] = tags ?? null;

            break;
          }
          case 'owner': {
            const owner = staticColumnData[staticCol.type];
            if (owner && typeof owner === 'object') {
              const ownerRecord = owner as {
                uid?: string;
                full_name?: string | null;
                email?: string | null;
              };
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to null
              const displayName = ownerRecord.full_name || ownerRecord.email || null;
              contractData[staticCol.fieldKey] = displayName;
              contractData[`__static_${staticCol.type}_data`] = owner;
              if (ownerRecord.uid && displayName) {
                entityNameCache.value.owner.set(ownerRecord.uid, displayName);
              }
            } else {
              contractData[staticCol.fieldKey] = null;
            }

            break;
          }
          case 'state': {
            // The grid draws the state cell from the object itself.
            contractData[staticCol.fieldKey] = staticColumnData[staticCol.type] ?? null;

            break;
          }
          default: {
            const staticData = staticColumnData[staticCol.type];
            if (staticData !== null && Array.isArray(staticData)) {
              contractData[staticCol.fieldKey] = extractStaticColumnNames(staticData);
              contractData[`__static_${staticCol.type}_data`] = staticData;
              updateEntityNameCache(staticData as { uid: string }[], staticCol.type);
            } else {
              contractData[staticCol.fieldKey] = null;
            }
          }
        }
      } else {
        const dynamicCol = col;
        const extraction = dynamicExtractionsMap.get(dynamicCol.uid);

        if (extraction) {
          let value: unknown = null;
          if (
            extraction.human_overwrite?.value !== undefined &&
            extraction.human_overwrite.value !== null
          ) {
            value = extraction.human_overwrite.value;
          } else if (extraction.value !== null) {
            value = extraction.value;
          }

          // Store raw value - formatting happens in ExtractionCellState with responseType
          contractData[dynamicCol.uid] = value ?? null;

          contractData[`__extraction_${dynamicCol.uid}`] = {
            extractionUid: extraction.uid,
            columnUid: dynamicCol.uid,
            extraction,
            column: dynamicCol,
          };
        } else {
          // Extraction doesn't exist yet - store null value and null extraction metadata
          // This allows ExtractionCellState to show spinner for "no extraction" state
          contractData[dynamicCol.uid] = null;
          contractData[`__extraction_${dynamicCol.uid}`] = {
            extractionUid: '',
            columnUid: dynamicCol.uid,
            extraction: null,
            column: dynamicCol,
          };
        }
      }
    }

    const contractNode: TreeNode = {
      key: contract.uid,
      data: contractData,
      leaf: false,
      children: undefined,
    };
    treeData.push(contractNode);
  }

  return treeData;
};

/**
 * Load contract files for a contract when it's expanded
 */
const loadContractFiles = async (contractUid: string): Promise<void> => {
  if (!viewData.value || loadingContractFiles.value.has(contractUid)) {
    return;
  }

  if (contractFilesCache.value.has(contractUid)) {
    updateContractChildren(contractUid);
    return;
  }

  loadingContractFiles.value.add(contractUid);

  try {
    const result = await ContractViewsService.getContractFilesForViewTable(
      viewData.value.uid,
      contractUid,
      {
        limit: 50,
        offset: 0,
        sort_key: 'created_at',
        sort_direction: 'desc',
      }
    );

    // Get all visible columns sorted (Unified list) - use memoized computed
    // The API now returns extractions in the same format as contracts - mixed static and dynamic, sorted by order_index
    const sortedCols = sortedColumns.value;
    if (!sortedCols) return;
    const { allColumns } = sortedCols;
    // The extractions array is already in the correct order (sorted by order_index, matching allColumns)
    const filesWithExtractions = result.results.map(([file, extractions]) => {
      // Build maps to identify static and dynamic extractions from the API response
      // The API returns extractions in order sorted by order_index (static and dynamic mixed)
      const staticColumnData: Record<
        string,
        unknown[] | string | string[] | Record<string, unknown> | null
      > = {};
      const dynamicExtractionsMap = new Map<string, ContractManagementExtractionInDb | null>();

      // Map extraction values using the corrected sort order
      const unsupportedStaticTypesForFiles = new Set<StaticColumnType>(['tags', 'matter', 'owner']);
      const unsupportedStaticCount = allColumns.filter(
        col => 'fieldKey' in col && unsupportedStaticTypesForFiles.has(col.type)
      ).length;
      const shouldSkipUnsupported =
        extractions.length === allColumns.length - unsupportedStaticCount;

      let extractionIndex = 0;
      for (const col of allColumns) {
        const isStatic = 'fieldKey' in col;
        const staticCol = isStatic ? col : null;
        const isUnsupportedStatic = staticCol && unsupportedStaticTypesForFiles.has(staticCol.type);

        if (isStatic && isUnsupportedStatic && shouldSkipUnsupported) {
          staticColumnData[staticCol.type] = null;
          continue;
        }

        // Safety check for bounds
        if (extractionIndex >= extractions.length) continue;
        const extractionValue = extractions[extractionIndex];
        extractionIndex += 1;

        if (isStatic && staticCol) {
          // Static column - extractionValue is an array of objects or null
          switch (staticCol.type) {
            case 'created_at':
            case 'type':
            case 'state': {
              // On a file row the state is the file's own `EffectiveState`.
              staticColumnData[staticCol.type] =
                typeof extractionValue === 'string' ? extractionValue : null;

              break;
            }
            case 'owner': {
              staticColumnData[staticCol.type] =
                extractionValue && typeof extractionValue === 'object' && 'uid' in extractionValue
                  ? extractionValue
                  : null;

              break;
            }
            case 'tags': {
              staticColumnData[staticCol.type] =
                Array.isArray(extractionValue) &&
                extractionValue.every(item => typeof item === 'string')
                  ? extractionValue
                  : null;

              break;
            }
            default:
              if (extractionValue !== null && Array.isArray(extractionValue)) {
                staticColumnData[staticCol.type] = extractionValue;
              } else {
                staticColumnData[staticCol.type] = null;
              }
          }
        } else {
          // Dynamic column - extractionValue is ContractManagementExtractionInDb or null
          const dynamicCol = col as ContractManagementViewInDb['columns'][number];
          if (extractionValue && typeof extractionValue === 'object' && 'uid' in extractionValue) {
            dynamicExtractionsMap.set(dynamicCol.uid, extractionValue);
          } else {
            dynamicExtractionsMap.set(dynamicCol.uid, null);
          }
        }
      }

      // Build the combined extractions array in the correct order (matching allColumns)
      const combinedExtractions: (
        | ContractManagementExtractionInDb
        | ContractualParty[]
        | ContractManagementMatterInDb[]
        | string // for created_at and type (document_type)
        | string[] // for tags (contract_tags)
        | null
      )[] = [];

      for (const col of allColumns) {
        if ('fieldKey' in col) {
          // Static column - get from staticColumnData
          const staticCol = col;
          if (
            staticCol.type === 'created_at' ||
            staticCol.type === 'type' ||
            staticCol.type === 'tags'
          ) {
            combinedExtractions.push(null);
            continue;
          }
          combinedExtractions.push(
            (staticColumnData[staticCol.type] as
              | ContractualParty[]
              | ContractManagementMatterInDb[]
              | string
              | string[]
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
              | null) || null
          );
        } else {
          // Dynamic column - get from dynamicExtractionsMap
          const dynamicCol = col;
          combinedExtractions.push(dynamicExtractionsMap.get(dynamicCol.uid) ?? null);
        }
      }

      return [file, combinedExtractions] as [
        ContractManagementContractFileInDb,
        (
          | ContractManagementExtractionInDb
          | ContractualParty[]
          | ContractManagementMatterInDb[]
          | string // for created_at and type (document_type)
          | string[] // for tags (contract_tags)
          | null
        )[],
      ];
    });

    contractFilesCache.value.set(contractUid, {
      files: filesWithExtractions,
      total: result.result_count,
    });

    updateContractChildren(contractUid);
  } catch (error_) {
    const errorMessage = error_ instanceof Error ? error_.message : 'Failed to load contract files';
    message.error(errorMessage);
    logger.error('Error loading contract files:', error_);
  } finally {
    loadingContractFiles.value.delete(contractUid);
  }
};

/**
 * Update contract node with file children from cache
 */
const updateContractChildren = (contractUid: string): void => {
  const cache = contractFilesCache.value.get(contractUid);
  if (!cache || !viewData.value) return;
  const sortedCols = sortedColumns.value;
  if (!sortedCols) return;
  const { allColumns } = sortedCols;
  const contractNode = treeNodeData.value.find(node => node.key === contractUid);
  if (!contractNode) return;

  const fileNodes: TreeNode[] = cache.files.map(([file, extractions], fileIndex) => {
    const fileData: Record<string, unknown> = {
      name:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
        file.vault_file?.current_revision?.file_name || file.description || `File ${fileIndex + 1}`,
      uid: file.uid,
      contractUid,
      type: file.type,
      description: file.description,
      created_at: file.created_at,
      updated_at: file.updated_at,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      vaultFileUid: file.vault_file?.uid || null,
      vaultFileName: file.vault_file?.current_revision?.file_name ?? '',
      // Shown with the state cell when sending the file for signature failed.
      signatureFailureReason: file.signature_failure_reason,
    };

    for (const [colIndex, col] of allColumns.entries()) {
      if (colIndex >= extractions.length) {
        const key = 'fieldKey' in col ? col.fieldKey : col.uid;
        fileData[key] = null;
        continue;
      }

      const extractionValue = extractions[colIndex];

      if ('fieldKey' in col) {
        const staticCol = col;
        switch (staticCol.type) {
          case 'created_at': {
            fileData[staticCol.fieldKey] = file.created_at;

            break;
          }
          case 'type': {
            fileData[staticCol.fieldKey] = file.type;

            break;
          }
          case 'tags': {
            // Contract file rows don't include tags from the API, so leave empty
            fileData[staticCol.fieldKey] = null;

            break;
          }
          case 'state': {
            fileData[staticCol.fieldKey] =
              typeof extractionValue === 'string' ? extractionValue : null;

            break;
          }
          case 'owner': {
            if (
              extractionValue &&
              typeof extractionValue === 'object' &&
              'uid' in extractionValue
            ) {
              const owner = extractionValue as {
                uid?: string;
                full_name?: string | null;
                email?: string | null;
              };
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must normalise to null
              const displayName = owner.full_name || owner.email || null;
              fileData[staticCol.fieldKey] = displayName;
              fileData[`__static_${staticCol.type}_data`] = extractionValue;
              if (owner.uid && displayName) {
                entityNameCache.value.owner.set(owner.uid, displayName);
              }
            } else {
              fileData[staticCol.fieldKey] = null;
            }

            break;
          }
          default:
            if (extractionValue !== null && Array.isArray(extractionValue)) {
              fileData[staticCol.fieldKey] = extractStaticColumnNames(extractionValue);
              fileData[`__static_${staticCol.type}_data`] = extractionValue;
              updateEntityNameCache(extractionValue as { uid: string }[], staticCol.type);
            } else {
              fileData[staticCol.fieldKey] = null;
            }
        }
      } else {
        const dynamicCol = col;
        if (extractionValue && typeof extractionValue === 'object' && 'uid' in extractionValue) {
          const extraction = extractionValue;
          let value: unknown = null;
          if (
            extraction.human_overwrite?.value !== undefined &&
            extraction.human_overwrite.value !== null
          ) {
            value = extraction.human_overwrite.value;
          } else if (extraction.value !== null) {
            value = extraction.value;
          }

          // Store raw value - formatting happens in ExtractionCellState with responseType
          fileData[dynamicCol.uid] = value ?? null;

          fileData[`__extraction_${dynamicCol.uid}`] = {
            extractionUid: extraction.uid,
            columnUid: dynamicCol.uid,
            extraction,
            column: dynamicCol,
            contractUid,
            fileUid: file.uid,
          };
        } else {
          // Extraction doesn't exist yet - store null value and null extraction metadata
          // This allows ExtractionCellState to show spinner for "no extraction" state
          fileData[dynamicCol.uid] = null;
          fileData[`__extraction_${dynamicCol.uid}`] = {
            extractionUid: '',
            columnUid: dynamicCol.uid,
            extraction: null,
            column: dynamicCol,
            contractUid,
            fileUid: file.uid,
          };
        }
      }
    }

    return {
      key: file.uid,
      data: fileData,
      leaf: true,
    };
  });

  contractNode.children = fileNodes;
  treeNodeData.value = [...treeNodeData.value];
};

/**
 * Transform API columns to ColumnDef format for the grid
 * UPDATED: Uses the unified 'allColumns' list to ensure visual order matches order_index
 */
const transformColumnsToColumnDefs = (view: ContractManagementViewInDb): ColumnDef[] => {
  const baseColumns: ColumnDef[] = [
    {
      headerName: '#',
      field: '__rowNumber',
      width: 60,
      minWidth: 60,
      frozen: true,
      alignFrozen: 'left',
      isRowNumber: true,
    },
  ];

  const nameColumn: ColumnDef = {
    field: 'name',
    headerName: 'Name',
    width: 300,
    minWidth: 250,
    expander: true,
    isStaticColumn: true,
    sortKey: 'name',
    filters: view.name_filters,
  };

  // Use the unified sorted list so visual order respects order_index
  // regardless of whether it's static or dynamic
  const { allColumns } = getAllVisibleColumnsSorted(view);

  const dataColumnDefs: ColumnDef[] = allColumns.map(col => {
    if ('fieldKey' in col) {
      // Static Column
      const staticCol = col;
      // Get entity filters for static columns
      // The backend stores these filters on the view and applies them automatically when fetching table data
      let filters: AndCriterionOutput | null = null;
      switch (staticCol.type) {
        case 'contractual_party': {
          filters = view.contractual_party_filters;

          break;
        }
        case 'matter': {
          filters = view.matter_filters;

          break;
        }
        case 'owner': {
          filters = view.owner_filters;

          break;
        }
        case 'effective_from': {
          filters = view.effective_from_filters;

          break;
        }
        case 'effective_to': {
          filters = view.effective_to_filters;

          break;
        }
        case 'created_at': {
          filters = view.created_at_filters;

          break;
        }
        case 'type': {
          filters = view.document_type_filters;

          break;
        }
        case 'tags': {
          filters = view.contract_tags_filters;

          break;
        }
        case 'state': {
          // Two stored filters on one column; either marks it filtered.
          filters =
            view.contract_state_filters.criteria.length > 0
              ? view.contract_state_filters
              : view.amendment_state_filters;

          break;
        }
        // No default
      }
      return {
        field: staticCol.fieldKey,
        headerName: staticCol.name,
        width: 200,
        minWidth: 150,
        isStaticColumn: true,
        // The backend sorts static columns by the same key it filters them by —
        // the column's type (the state column: the contract's state), not the
        // synthetic `__static_*` grid field. Only the types it can really order
        // by get a key; the rest stay unsortable so we don't offer a caret that
        // silently returns id order.
        sortKey: staticColumnSortKey(staticCol.type),
        filters,
      };
    } else {
      // Dynamic Column
      const dynamicCol = col;

      // Normalize filters from API format to internal format
      let normalizedFilters: AndCriterionInput | null = null;
      if (dynamicCol.filters.criteria.length > 0) {
        const normalized = normalizeFilterFromApi(dynamicCol.filters);
        normalizedFilters = normalized as AndCriterionInput | null;
      }

      const columnDef: ColumnDef = {
        field: dynamicCol.uid,
        headerName: dynamicCol.display_name,
        width: 200,
        minWidth: 150,
        filters: normalizedFilters,
        responseType: dynamicCol.extraction_prompt.response_type,
      };

      if (
        dynamicCol.display_name.toLowerCase().includes('status') ||
        dynamicCol.display_name.toLowerCase().includes('risk level') ||
        dynamicCol.display_name.toLowerCase().includes('risk')
      ) {
        columnDef.isStatus = true;
      }
      return columnDef;
    }
  });

  return [...baseColumns, nameColumn, ...dataColumnDefs];
};

// --- Data Loading ---
const loadViewDetails = async (showTableOverlay = false): Promise<boolean> => {
  if (!currentViewId.value) {
    error.value = 'View ID is required';
    return false;
  }

  // Only show full-page loading if we don't have viewData yet (initial load)
  // If we already have viewData, we're just refreshing - use overlay instead
  const isInitialLoad = !viewData.value;

  if (isInitialLoad) {
    isLoading.value = true;
  }

  error.value = null;
  // Ensure refresh state is cleared on initial load
  if (!showTableOverlay) {
    resetRefreshState();
  }
  loadingBar.start();

  try {
    const view = await ContractViewsService.getById(currentViewId.value);
    viewData.value = view;
    columnDefs.value = transformColumnsToColumnDefs(view);
    void loadFilterOptions();
    await loadTableData({ showOverlay: showTableOverlay });
    loadingBar.finish();
    return true;
  } catch (error_) {
    const errorMessage =
      error_ instanceof Error ? error_.message : 'Failed to load view details. Please try again.';
    error.value = errorMessage;
    message.error(errorMessage);
    loadingBar.error();
    // Ensure refresh state is cleared on error
    resetRefreshState();
    return false;
  } finally {
    if (isInitialLoad) {
      isLoading.value = false;
    }
  }
};

interface LoadTableDataOptions {
  preserveExpandedState?: boolean;
  showOverlay?: boolean;
  isAutoRefresh?: boolean;
}

const loadTableData = async (options: LoadTableDataOptions = {}): Promise<void> => {
  if (!viewData.value || !currentViewId.value) {
    return;
  }

  const { preserveExpandedState = false, showOverlay = true, isAutoRefresh = false } = options;

  // --- Overlapping Fetch Prevention ---
  // If a table fetch is already in flight (from any source), skip auto-refresh ticks.
  // Without this, slow/hanging backend responses cause requests to pile up every 15s,
  // overwhelming workers — especially when filter_options computation is expensive.
  if (isAutoRefresh && (isLoading.value || isRefreshingTable.value || isTableFetching.value)) {
    return;
  }

  // If this is a MANUAL action (filter, page change, sort), we must STOP the auto-refresh
  // cycle immediately. We don't want a background timer firing while the user is interacting.
  // We will restart it in the finally block.
  if (!isAutoRefresh) {
    stopAutoRefresh();
  }

  const fetchId = (tableFetchCounter += 1);
  latestTableFetchId.value = fetchId;

  // Store previous data if we're showing overlay (refresh scenario)
  // Only show overlay if we have existing data to display
  const shouldShowOverlay = showOverlay && treeNodeData.value.length > 0;

  if (shouldShowOverlay) {
    // Use structuredClone for better performance than JSON.parse/stringify
    // Falls back to JSON method if structuredClone is not available
    try {
      previousTreeNodeData.value = structuredClone(treeNodeData.value);
    } catch {
      // Fallback for environments without structuredClone support
      previousTreeNodeData.value = JSON.parse(JSON.stringify(treeNodeData.value));
    }
    isRefreshingTable.value = true;
    overlayTableFetchId.value = fetchId;
  } else {
    // Ensure refresh state is cleared if not showing overlay and no other overlay is active
    if (!isRefreshingTable.value) {
      resetRefreshState();
    }
  }

  isTableFetching.value = true;

  try {
    // Only calculate expanded UIDs if we need to preserve state
    const expandedContractUids = preserveExpandedState
      ? treeNodeData.value
          .filter(node => node.children && node.children.length > 0)
          .map(node => node.key)
      : [];

    const result = await ContractViewsService.getViewTable(currentViewId.value, {
      text: searchQuery.value || '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value,
      column_filters: buildColumnFilters(),
    });

    if (fetchId !== latestTableFetchId.value) {
      return;
    }

    rawTableData.value = cloneTableData(result);
    totalCount.value = result.result_count;

    treeNodeData.value = transformTableDataToTreeNodes(result.results, viewData.value);

    if (preserveExpandedState && expandedContractUids.length > 0) {
      await Promise.all(
        expandedContractUids.map(contractUid => {
          contractFilesCache.value.delete(contractUid);
          return loadContractFiles(contractUid);
        })
      );
    }
  } catch (error_) {
    if (fetchId !== latestTableFetchId.value) {
      return;
    }
    const errorMessage =
      error_ instanceof Error ? error_.message : 'Failed to load table data. Please try again.';
    message.error(errorMessage);
    throw error_;
  } finally {
    isTableFetching.value = false;

    // Always reset refresh state after loading completes
    if (shouldShowOverlay && overlayTableFetchId.value === fetchId) {
      resetRefreshState();
      overlayTableFetchId.value = null;
    }

    // If we stopped the refresh cycle earlier (because this was a manual action),
    // we restart it now that the manual action is complete.
    if (!isAutoRefresh && !viewComplete.value) {
      startAutoRefresh();
    }
  }
};

// Filter options depend on the view's scope, not on the table query, so they
// load on their own — alongside the view and whenever values may have changed
// (extractions landing, edits) — never per search, filter, sort or page.
let filterOptionsFetchCounter = 0;
let isFilterOptionsFetching = false;

const loadFilterOptions = async (options: { isAutoRefresh?: boolean } = {}): Promise<void> => {
  const viewUid = currentViewId.value;
  if (!viewUid) return;
  // Same pile-up guard as the table: a slow response must not stack ticks.
  if (options.isAutoRefresh && isFilterOptionsFetching) return;

  const fetchId = (filterOptionsFetchCounter += 1);
  isFilterOptionsFetching = true;
  try {
    const result = await ContractViewsService.getViewTableFilterOptions(viewUid);
    if (fetchId !== filterOptionsFetchCounter) return;

    const converted: Record<string, (string | null)[]> = {};
    for (const [key, values] of Object.entries(result.filter_options)) {
      const strings = values.map(v => (v === null || v === undefined ? null : safeStringify(v)));
      converted[key] = dateFilterKeys.value.has(key) ? toFilterDays(strings) : strings;
    }
    filterOptions.value = converted;
  } catch (error_) {
    if (fetchId !== filterOptionsFetchCounter) return;
    message.error(
      error_ instanceof Error ? error_.message : 'Failed to load filter options. Please try again.'
    );
  } finally {
    if (fetchId === filterOptionsFetchCounter) isFilterOptionsFetching = false;
  }
};

// The search field owns the debounce (1s — the table request is heavy) and
// fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  if (!viewData.value) return;
  currentPage.value = 1;
  contractFilesCache.value.clear();
  await loadTableData({ showOverlay: true });
}

// --- Filtering Logic ---
// Note: filteredData removed - use treeNodeData directly in template

/**
 * Filter keys holding dates or datetimes. The backend compares these by whole
 * UTC day, so the strip offers one entry per day rather than per timestamp.
 */
const dateFilterKeys = computed<Set<string>>(() => {
  const keys = new Set<string>(['created_at', 'effective_from', 'effective_to']);
  for (const column of viewData.value?.columns ?? []) {
    const responseType = column.extraction_prompt.response_type;
    if (typeof responseType !== 'string') continue;
    const elementType = getElementType(responseType);
    if (elementType === 'date' || elementType === 'datetime') keys.add(column.uid);
  }
  return keys;
});

/** Distinct UTC days of a date column's options; empties are kept as one entry. */
function toFilterDays(values: (string | null)[]): (string | null)[] {
  const days = new Set<string | null>();
  for (const value of values) {
    if (value === null || value === '') days.add(value);
    else {
      const day = utcDayFromValue(value);
      if (day) days.add(day);
    }
  }
  return [...days];
}

const filterConfig = computed<FilterConfig[]>(() => {
  const configs: FilterConfig[] = [];
  if (!viewData.value) return configs;

  const staticColumns = getStaticColumns(viewData.value);
  for (const staticCol of staticColumns) {
    // The state column filters by two keys, the contract's and the amendments' state.
    const filters: FilterConfig[] =
      staticCol.type === 'state'
        ? [
            { key: 'contract_state', label: 'Contract state' },
            { key: 'amendment_state', label: 'Amendment state' },
          ]
        : [{ key: staticCol.type, label: staticCol.name }];
    for (const filter of filters) {
      if (filter.key in filterOptions.value && filterOptions.value[filter.key].length > 0) {
        configs.push(filter);
      }
    }
  }

  for (const column of viewData.value.columns) {
    const columnUid = column.uid;
    if (columnUid in filterOptions.value && filterOptions.value[columnUid].length > 0) {
      configs.push({
        key: columnUid,
        label: column.display_name || columnUid,
      });
    }
  }

  return configs;
});

const valueLabelMap = computed<Record<string, Record<string | number, string>>>(() => {
  const map: Record<string, Record<string | number, string>> = {};
  if (!viewData.value) return map;

  const staticColumns = getStaticColumns(viewData.value);

  for (const staticCol of staticColumns) {
    const filterKey = staticCol.type;
    map[filterKey] = {};

    // For entity-based static columns, use entityNameCache for UID→name mapping.
    // Effective dates filter by ISO day now, so they read through displayLabel.
    if (isEntityBasedStaticColumn(staticCol.type) && !dateFilterKeys.value.has(staticCol.type)) {
      const cache = entityNameCache.value[staticCol.type];
      for (const [uid, name] of cache.entries()) {
        map[filterKey][uid] = name;
      }
    }
    // For primitive static columns (created_at, type, tags), filter options already contain
    // the display values, so no mapping is needed - FilterBar will use the values directly
  }

  // State filters carry `EffectiveState` values; they read as the state labels.
  const stateLabels = Object.fromEntries(
    CONTRACT_ICON_STATES.map(state => [state, contractStateLabel(state)])
  );
  map.contract_state = stateLabels;
  map.amendment_state = stateLabels;

  return map;
});

function buildColumnFilters(): { [key: string]: string[] } {
  const filters: { [key: string]: string[] } = {};

  // Add user-selected filters from FilterBar
  // Note: Static column filters (contractual_party_filters, matter_filters, …)
  // are automatically applied by the backend when fetching table data, so we don't need to include them here
  for (const key of Object.keys(activeFilters.value)) {
    const values = pickedValues(key).map(v => (v === '(Empty)' ? '' : v));
    if (values.length > 0) filters[key] = values;
  }

  return filters;
}

/**
 * The Filters button is a toggle: opening seeds the strip with the first
 * filterable column, closing it drops every active filter — the same contract
 * the entity lists use, so the two pages behave alike.
 */
function toggleFilterBar(): void {
  const on = !(showFilterBar.value || activeFilterCount.value > 0);
  if (!on) {
    showFilterBar.value = false;
    openFilterKeys.value = [];
    openChip.value = null;
    if (activeFilterCount.value > 0) handleFiltersUpdate({});
    return;
  }
  showFilterBar.value = true;
  if (!openFilterKeys.value.length && filterConfig.value.length > 0) {
    const first = filterConfig.value[0];
    openFilterKeys.value = [first.key];
    openChip.value = first.key;
  }
}

function handleFiltersUpdate(filters: Record<string, unknown>): void {
  activeFilters.value = filters;
  currentPage.value = 1;
  contractFilesCache.value.clear();
  void loadTableData();
}

// --- Filter strip ----------------------------------------------------------
/** Which chips are on the strip, and which one has its value panel open. */
const openFilterKeys = ref<string[]>([]);
const openChip = ref<string | null>(null);
const valueSearch = ref<Record<string, string>>({});

const activeFilterCount = computed<number>(() =>
  Object.values(activeFilters.value).reduce<number>(
    (total, values) => total + (Array.isArray(values) ? values.length : 0),
    0
  )
);

const availableFilters = computed(() =>
  filterConfig.value.filter(filter => !openFilterKeys.value.includes(filter.key))
);

function filterLabel(key: string): string {
  return filterConfig.value.find(filter => filter.key === key)?.label ?? key;
}

function pickedValues(key: string): string[] {
  const values = activeFilters.value[key];
  if (!Array.isArray(values)) return [];
  const strings = values.map(value => String(value));
  // A stale link can still carry an effective-date uid, which now matches
  // nothing; date keys keep only values that name a day.
  return dateFilterKeys.value.has(key)
    ? strings.filter(value => value === '' || value === '(Empty)' || utcDayFromValue(value))
    : strings;
}

/**
 * Entity columns store uids but read as names, so the chip and its panel show
 * the mapped label while everything written back stays the raw value.
 */
function displayLabel(key: string, value: string): string {
  // Indexing a Record is typed as always-present; at runtime a column with no
  // mapping (every non-entity column) yields undefined, so it is read as such.
  const labels = valueLabelMap.value[key] as Record<string, string> | undefined;
  const label = labels?.[value];
  if (label) return label;
  if (value === '') return '(Empty)';
  if (dateFilterKeys.value.has(key)) return formatDateInputDisplay(value) || value;
  return value;
}

const STATE_FILTER_KEYS = new Set(['contract_state', 'amendment_state']);

function chipValues(key: string): string[] {
  return pickedValues(key).map(value => displayLabel(key, value));
}

function filterValues(key: string): GaiaFilterValue[] {
  const term = (valueSearch.value[key] ?? '').trim().toLowerCase();
  const picked = pickedValues(key);
  return (filterOptions.value[key] ?? [])
    .map(value => value ?? '')
    .map(value => ({ raw: value, label: displayLabel(key, value) }))
    .filter(option => !term || option.label.toLowerCase().includes(term))
    .sort((a, b) => {
      // States arrive in severity order, which reads better than the alphabet.
      if (STATE_FILTER_KEYS.has(key)) return 0;
      // ISO days sort as strings; newest first, "(Empty)" last.
      if (dateFilterKeys.value.has(key)) return b.raw.localeCompare(a.raw);
      return a.label.localeCompare(b.label);
    })
    .map(option => ({
      key: option.raw,
      label: option.label,
      checked: picked.includes(option.raw),
    }));
}

function toggleFilterValue(key: string, value: string): void {
  const current = pickedValues(key);
  const next = current.includes(value)
    ? current.filter(picked => picked !== value)
    : [...current, value];
  handleFiltersUpdate({ ...activeFilters.value, [key]: next });
}

/** The chip hands back a label, so it has to be resolved to the stored value. */
function removeChipValue(key: string, label: string): void {
  const value = pickedValues(key).find(picked => displayLabel(key, picked) === label);
  if (value !== undefined) toggleFilterValue(key, value);
}

function addFilter(key: string): void {
  openFilterKeys.value = [...openFilterKeys.value, key];
  openChip.value = key;
}

function removeFilter(key: string): void {
  openFilterKeys.value = openFilterKeys.value.filter(open => open !== key);
  openChip.value = null;
  if (pickedValues(key).length > 0) {
    handleFiltersUpdate({ ...activeFilters.value, [key]: [] });
  }
}

function clearAllFilters(): void {
  handleFiltersUpdate({});
}

function clearChipValues(key: string): void {
  handleFiltersUpdate({ ...activeFilters.value, [key]: [] });
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  // Trigger manual load (resets timer)
  void loadTableData();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
  // Trigger manual load (resets timer)
  void loadTableData();
}

/**
 * Clicking a header sorts by it; clicking the column already sorted flips the
 * direction. Dates open newest-first, everything else A→Z — the same contract
 * the views list uses.
 */
function handleSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    sortDirection.value = key === 'created_at' ? 'desc' : 'asc';
  }
  currentPage.value = 1;
  contractFilesCache.value.clear();
  // Trigger manual load (resets timer)
  void loadTableData();
}

// --- Event Handlers ---
const handleCreateColumn = (): void => {
  showCreateColumnDrawer.value = true;
};

const handleColumnCreated = async (): Promise<void> => {
  await loadViewDetails(true);
};

/** The view's folders/contracts changed — its rows did too. */
const handleSourcesSaved = async (): Promise<void> => {
  currentPage.value = 1;
  contractFilesCache.value.clear();
  await loadViewDetails(true);
};

const handleShowColumns = (): void => {
  showColumnsPopover.value = !showColumnsPopover.value;
};

/**
 * Persist a new column order after a drag in the Columns popover.
 *
 * Positions are reassigned from 0 across the shown columns in their new
 * sequence, then the hidden ones are parked after them — so indices stay
 * unique (the grid and the backend pivot disagree about ties) and un-hiding a
 * column appends it rather than dropping it into the middle of the row.
 *
 * Static positions live on the view and go up in one request; each view column
 * carries its own. Only the columns whose index actually moved are written.
 */
const handleReorderColumns = async (order: ViewColumnOrderItem[]): Promise<void> => {
  const view = viewData.value;
  if (!view || order.length === 0) return;

  const staticOrder = new Map<ConfigurableStaticColumnType, number>();
  const dynamicOrder = new Map<string, number>();
  let nextIndex = 0;

  for (const item of order) {
    if (item.kind === 'static') staticOrder.set(item.type, nextIndex);
    else dynamicOrder.set(item.uid, nextIndex);
    nextIndex += 1;
  }

  for (const type of Object.keys(STATIC_COLUMN_CONFIGS) as ConfigurableStaticColumnType[]) {
    if (staticOrder.has(type)) continue;
    staticOrder.set(type, nextIndex);
    nextIndex += 1;
  }
  for (const column of view.columns) {
    if (dynamicOrder.has(column.uid)) continue;
    dynamicOrder.set(column.uid, nextIndex);
    nextIndex += 1;
  }

  // Built by assignment rather than as a literal: the field name comes from
  // STATIC_COLUMN_CONFIGS, so it isn't a literal key at the call site.
  const staticUpdate: ContractManagementViewUpdate = {};
  let staticChanged = false;
  for (const [type, index] of staticOrder) {
    const config = STATIC_COLUMN_CONFIGS[type];
    if (view[config.orderIndexKey] === index) continue;
    Object.assign(staticUpdate, { [config.orderIndexKey]: index });
    staticChanged = true;
  }

  const movedColumns = view.columns.filter(
    column => dynamicOrder.get(column.uid) !== column.order_index
  );

  if (!staticChanged && movedColumns.length === 0) return;

  try {
    loadingBar.start();
    if (staticChanged) {
      await ContractViewsService.update(view.uid, staticUpdate);
    }
    await Promise.all(
      movedColumns.map(column =>
        ContractViewsService.updateColumn(column.uid, {
          order_index: dynamicOrder.get(column.uid) ?? column.order_index,
        })
      )
    );
    // The table has to come back with it: the backend returns each row's values
    // positionally, in the columns' order_index order.
    contractFilesCache.value.clear();
    await loadViewDetails(true);
    message.success('Column order updated');
    loadingBar.finish();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to reorder columns';
    message.error(errorMsg);
    loadingBar.error();
  }
};

const handleToggleColumnVisibility = async (columnUid: string): Promise<void> => {
  if (!viewData.value) return;

  const column = viewData.value.columns.find(col => col.uid === columnUid);
  if (!column) return;

  const newVisibility = !column.visible;

  try {
    loadingBar.start();
    await ContractViewsService.updateColumnVisibility(columnUid, newVisibility);
    await loadViewDetails(true);
    message.success(`Column ${newVisibility ? 'shown' : 'hidden'} successfully`);
    loadingBar.finish();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to toggle column visibility';
    message.error(errorMsg);
    loadingBar.error();
  }
};

const handleToggleStaticColumnVisibility = async (
  staticColumnType: StaticColumnType
): Promise<void> => {
  if (!viewData.value) return;

  // 'name' column has no visibility to toggle — it's always visible
  if (staticColumnType === 'name') return;

  const config: {
    nameKey: keyof ContractManagementViewInDb;
    visibleKey: keyof ContractManagementViewInDb;
    orderIndexKey: keyof ContractManagementViewInDb;
    fieldKey: string;
  } = STATIC_COLUMN_CONFIGS[staticColumnType];

  const currentVisibility = viewData.value[config.visibleKey] as boolean;
  const newVisibility = !currentVisibility;

  const updateData: ContractManagementViewUpdate = {
    [config.visibleKey]: newVisibility,
  };

  try {
    loadingBar.start();
    await ContractViewsService.update(viewData.value.uid, updateData);
    await loadViewDetails(true);
    message.success(`Column ${newVisibility ? 'shown' : 'hidden'} successfully`);
    loadingBar.finish();
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to toggle column visibility';
    message.error(errorMsg);
    loadingBar.error();
  }
};

const handleEditColumn = (columnUid: string): void => {
  initialDrawerTab.value = 'settings'; // Reset to settings when opening from column popover
  editingColumnUid.value = columnUid;
  showColumnsPopover.value = false;
  showUpdateColumnDrawer.value = true;
};

const handleEditStaticColumnFilters = (staticColumnType: StaticColumnType): void => {
  initialDrawerTab.value = 'settings'; // Reset to settings when opening from column popover
  editingStaticColumnType.value = staticColumnType;
  showColumnsPopover.value = false;
  showEditStaticColumnDrawer.value = true;
};

// Track initial tab for drawers
const initialDrawerTab = ref<string>('settings');

// Handle filter icon click from column header
const handleEditColumnFilters = (data: { field: string; isStaticColumn: boolean }): void => {
  // Set initial tab to filters
  initialDrawerTab.value = 'filters';

  if (data.isStaticColumn) {
    // Extract static column type from field
    // Handle different field key patterns:
    // - '__static_contractual_party' -> 'contractual_party'
    // - '__created_at' -> 'created_at'
    // - '__type' -> 'type'
    // - '__tags' -> 'tags'
    // - 'name' -> 'name' (Name column has no prefix)
    let staticColumnType: StaticColumnType | null = null;

    switch (data.field) {
      case '__created_at': {
        staticColumnType = 'created_at';
        break;
      }
      case '__type': {
        staticColumnType = 'type';
        break;
      }
      case '__tags': {
        staticColumnType = 'tags';
        break;
      }
      case 'name': {
        staticColumnType = 'name';
        break;
      }
      default: {
        if (data.field.startsWith('__static_')) {
          staticColumnType = data.field.replace('__static_', '') as StaticColumnType;
        }
      }
    }

    if (
      staticColumnType &&
      [
        'contractual_party',
        'matter',
        'owner',
        'effective_from',
        'effective_to',
        'created_at',
        'type',
        'tags',
        'state',
        'name',
      ].includes(staticColumnType)
    ) {
      editingStaticColumnType.value = staticColumnType;
      showEditStaticColumnDrawer.value = true;
    }
  } else {
    // Dynamic column - use column UID (field is the UID)
    editingColumnUid.value = data.field;
    showUpdateColumnDrawer.value = true;
  }
};

const handleStaticColumnUpdated = async (): Promise<void> => {
  await loadViewDetails(true);
  showEditStaticColumnDrawer.value = false;
  editingStaticColumnType.value = null;
  initialDrawerTab.value = 'settings'; // Reset tab
};

const handleColumnUpdated = async (): Promise<void> => {
  await loadViewDetails(true);
  showUpdateColumnDrawer.value = false;
  editingColumnUid.value = null;
  initialDrawerTab.value = 'settings'; // Reset tab
};

const handleColumnDeleted = async (): Promise<void> => {
  await loadViewDetails(true);
  showUpdateColumnDrawer.value = false;
  editingColumnUid.value = null;
};

// Get filter options for a specific column
function getFilterOptionsForColumn(columnUid: string): { label: string; value: string | number }[] {
  if (!(columnUid in filterOptions.value)) {
    return [];
  }
  const options = filterOptions.value[columnUid];
  if (options.length === 0) {
    return [];
  }

  // Convert to the format expected by RecursiveQueryBuilder
  return options
    .filter(opt => opt !== null)
    .map(opt => {
      // Try to get label from valueLabelMap if available
      const labelMap =
        columnUid in valueLabelMap.value ? valueLabelMap.value[columnUid] : undefined;
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      const label = labelMap?.[opt as string | number] || opt;
      return {
        label,
        value: opt,
      };
    });
}

const handleEditCell = (data: {
  nodeKey: string | number;
  field: string;
  cellData: Record<string, unknown>;
}): void => {
  if (!viewData.value) return;
  const field = data.field;

  // Disable editing for Name column
  if (field === 'name') {
    message.warning('Name column cannot be edited.');
    return;
  }

  // Handle static column editing - only allowed on contract files, not contract cells
  if (field.startsWith('__static_')) {
    // Check if this is a contract file (has contractUid) or a contract cell (no contractUid)
    const isContractFile = Boolean(data.cellData.contractUid);
    if (!isContractFile) {
      message.warning(
        'Static columns can only be edited from contract files, not from contract rows.'
      );
      return;
    }
    handleEditStaticColumn(field, data.cellData);
    return;
  }

  const columnUid = field;
  const column = viewData.value.columns.find(col => col.uid === columnUid);

  if (!column) {
    message.warning('Column not found');
    return;
  }

  const extractionMetadata = data.cellData[`__extraction_${columnUid}`] as
    | {
        extractionUid: string;
        columnUid: string;
        extraction: ContractManagementExtractionInDb;
        column: ContractManagementViewInDb['columns'][number];
        contractUid?: string;
        fileUid?: string;
      }
    | undefined;

  if (!extractionMetadata) {
    message.warning('Extraction data not found for this cell');
    return;
  }

  editModalCellData.value = {
    columnUid,
    extraction: extractionMetadata.extraction,
    column,
    contractUid: extractionMetadata.contractUid,
    fileUid: extractionMetadata.fileUid,
  };

  showEditModal.value = true;
};

const handleEditSave = async (): Promise<void> => {
  void loadFilterOptions();
  await loadTableData({ preserveExpandedState: true });
};

/**
 * Handle editing static column from grid cell
 * Shows entities for a specific contract file
 */
const handleEditStaticColumn = (field: string, cellData: Record<string, unknown>): void => {
  // Extract static column type from field (e.g., '__static_contractual_party' -> 'contractual_party')
  const staticColumnType = field.replace('__static_', '') as StaticColumnType;

  // Only the parties column is edited in place; its links live on the contract file.
  if (staticColumnType !== 'contractual_party') {
    message.warning('Unknown static column type');
    return;
  }

  // Get contract file UID from cell data
  const contractFileUid = (cellData.uid as string) || null;

  if (!contractFileUid) {
    message.warning('Contract file UID not found. Please click on a contract file row.');
    return;
  }

  // Get static column data
  const staticDataKey = `__static_${staticColumnType}_data`;
  const staticData = cellData[staticDataKey] as ContractualParty[] | undefined;

  const entities = staticData && Array.isArray(staticData) ? staticData : [];

  // Initialize staticColumnEditData if it's null
  staticColumnEditData.value ??= {
    staticColumnType: null,
    entities: [],
    contractFileUid: null,
  };

  staticColumnEditData.value = {
    staticColumnType,
    entities,
    contractFileUid,
  };

  showStaticColumnEditModal.value = true;
};

/**
 * Handle static column entity removed
 */
const handleStaticColumnEntityRemoved = async (): Promise<void> => {
  void loadFilterOptions();
  await loadTableData({ preserveExpandedState: true }); // Preserve expanded state, overlay is default
  // Don't close the modal automatically - let user close it manually after reviewing changes
};

/**
 * Handle static column entity added
 */
const handleStaticColumnEntityAdded = async (): Promise<void> => {
  void loadFilterOptions();
  await loadTableData({ preserveExpandedState: true }); // Preserve expanded state, overlay is default

  // Update staticColumnEditData with the refreshed data from the table
  // Use nextTick to ensure treeNodeData is fully updated
  await nextTick();
  if (
    staticColumnEditData.value &&
    staticColumnEditData.value.contractFileUid &&
    staticColumnEditData.value.staticColumnType
  ) {
    refreshStaticColumnEditData();
  }
};

/**
 * Refresh static column edit data from the updated table data
 */
function refreshStaticColumnEditData(): void {
  if (
    !staticColumnEditData.value ||
    !staticColumnEditData.value.contractFileUid ||
    !staticColumnEditData.value.staticColumnType
  ) {
    return;
  }

  const contractFileUid = staticColumnEditData.value.contractFileUid;
  const staticColumnType = staticColumnEditData.value.staticColumnType;
  const staticDataKey = `__static_${staticColumnType}_data`;

  // Find the contract file in treeNodeData (which is updated after loadTableData)
  let updatedEntities: ContractualParty[] | undefined = undefined;

  // Search through treeNodeData to find the file node
  for (const contractNode of treeNodeData.value) {
    if (contractNode.children) {
      for (const fileNode of contractNode.children) {
        if (fileNode.data && (fileNode.data as Record<string, unknown>).uid === contractFileUid) {
          const fileData = fileNode.data as Record<string, unknown>;
          const staticData = fileData[staticDataKey] as ContractualParty[] | undefined;
          if (staticData && Array.isArray(staticData)) {
            updatedEntities = staticData;
            break;
          }
        }
      }
      if (updatedEntities) break;
    }
  }

  // If not found in treeNodeData, try contractFilesCache
  if (!updatedEntities) {
    for (const [, cache] of contractFilesCache.value.entries()) {
      const fileData = cache.files.find(([file]) => file.uid === contractFileUid);
      if (fileData) {
        const [, extractions] = fileData;
        // Find the static column data - it should be at the index matching the static column
        if (!viewData.value) break;
        const staticColumns = getStaticColumns(viewData.value);
        const staticColIndex = staticColumns.findIndex(col => col.type === staticColumnType);
        if (staticColIndex !== -1 && staticColIndex < extractions.length) {
          const extractionValue = extractions[staticColIndex];
          if (
            Array.isArray(extractionValue) &&
            extractionValue.length > 0 &&
            typeof extractionValue[0] === 'object' &&
            'uid' in extractionValue[0]
          ) {
            updatedEntities = extractionValue as ContractualParty[];
            break;
          }
        }
      }
    }
  }

  // Update staticColumnEditData with the refreshed entities
  if (updatedEntities) {
    staticColumnEditData.value = {
      ...staticColumnEditData.value,
      entities: updatedEntities,
    };
  }
}

const handleContractExpand = async (nodeKey: string | number): Promise<void> => {
  const contractUid = String(nodeKey);
  const contractNode = treeNodeData.value.find(node => node.key === contractUid);
  if (!contractNode) {
    return;
  }

  if (!contractNode.children || contractNode.children.length === 0) {
    await loadContractFiles(contractUid);
  }
};

const handleGoToContract = (data: {
  contractUid: string;
  nodeKey: string | number | undefined;
}): void => {
  void router.push(contractRoute(data.contractUid));
};

const handleGoToContractFile = (data: {
  contractFileUid: string;
  contractUid: string;
  nodeKey: string | number | undefined;
}): void => {
  void router.push(contractFileRoute(data.contractFileUid));
};

const handleAddRevision = (data: { vaultFileUid: string; vaultFileName: string }): void => {
  revisionUploadFileUid.value = data.vaultFileUid;
  revisionUploadFileName.value = data.vaultFileName;
  showRevisionUploadModal.value = true;
};

// --- Lifecycle ---
onMounted(async () => {
  void contractTypeStore.ensureLoaded();
  if (currentViewId.value) {
    const success = await loadViewDetails();
    if (success) startAutoRefresh();
  }
});

watch(
  currentViewId,
  async newViewId => {
    if (newViewId) {
      const success = await loadViewDetails();
      if (success) startAutoRefresh();
    }
  },
  { immediate: false }
);

watch(
  viewComplete,
  isComplete => {
    if (isComplete) {
      stopAutoRefresh();
      return;
    }
    startAutoRefresh();
  },
  { immediate: false }
);

// --- Chrome ----------------------------------------------------------------
const pageX = gaiaShellPadding.pageX;
const hairline = gaiaNeutral.borderSubtle;
const stripSurface = gaiaNeutral.surfaceSoft;
const cardBorder = gaiaNeutral.borderCard;
/* The real grid's columns aren't known until the view loads, so the skeleton
   rides a plausible template: the frozen name track and four value tracks. */
const skeletonRowStyle = {
  gridTemplateColumns: 'minmax(220px, 1.4fr) repeat(4, minmax(140px, 1fr))',
};
const surface = gaiaNeutral.surface;
const scrim = gaiaNeutral.surface;

onUnmounted(() => {
  stopAutoRefresh();
});
</script>

<template>
  <Layout fill position="absolute" content-padding="0" content-max-width="100%">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-management-views' }">{{ navLabels.views }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ viewTitle }}</n-breadcrumb-item>
    </template>

    <div class="view-detail" :data-view-complete="viewComplete">
      <!-- Title and the grid's own controls share one bar; both stay put while
         the grid scrolls under them. -->
      <div class="title-bar">
        <div style="min-width: 0">
          <gaia-header :level="1" variant="display" size="dialog" truncate :style="{ margin: 0 }">
            {{ viewTitle }}
          </gaia-header>
          <gaia-text size="micro" tone="faint" :style="{ display: 'block', marginTop: '1px' }">
            {{ viewSubtitle }}
          </gaia-text>
        </div>

        <n-flex align="center" :size="10" :wrap="false" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            placeholder="Search contracts"
            :disabled="isLoading || !!error"
            :debounce="1000"
            @search="applySearch"
          />

          <gaia-button size="medium" :disabled="isLoading || !!error" @click="toggleFilterBar">
            <template #icon>
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2 3h12l-4.5 5v4l-3 1.5v-5.5z" />
              </svg>
            </template>
            Filters
            <gaia-count-pill v-if="activeFilterCount" :style="{ marginLeft: '7px' }">
              {{ activeFilterCount }}
            </gaia-count-pill>
          </gaia-button>

          <gaia-button
            v-if="canEditSources"
            size="medium"
            :disabled="isLoading || !!error"
            @click="showSourcesModal = true"
          >
            <template #icon>
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linejoin="round"
              >
                <path d="M1.5 4.5v8.5h13V6h-6.5L6.5 4.5z" />
              </svg>
            </template>
            Contracts
          </gaia-button>

          <!-- The columns popover keeps its existing component: it owns the
             shown/hidden split, the per-row edit + eye, and drag reordering. -->
          <div v-if="viewData" data-tour="views.columns">
            <ViewColumnPopover
              v-model:show="showColumnsPopover"
              :columns="viewData.columns"
              :static-columns="getStaticColumnsForPopover(viewData)"
              @toggle-visibility="handleToggleColumnVisibility"
              @toggle-static-visibility="handleToggleStaticColumnVisibility"
              @edit="handleEditColumn"
              @edit-static="handleEditStaticColumnFilters"
              @reorder="handleReorderColumns"
            >
              <template #trigger>
                <gaia-button
                  size="medium"
                  :disabled="isLoading || !!error"
                  @click="handleShowColumns"
                >
                  <template #icon>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path d="M2.5 2.5h11v11h-11z" />
                      <path d="M6.3 2.5v11M9.7 2.5v11" />
                    </svg>
                  </template>
                  Columns
                </gaia-button>
              </template>
            </ViewColumnPopover>
          </div>
        </n-flex>
      </div>

      <!-- Filter strip: one chip per opened column, each with its value panel. -->
      <div v-if="viewData && (showFilterBar || activeFilterCount)" class="filter-strip">
        <gaia-filter-bar
          :can-add="availableFilters.length > 0"
          :show-reset="activeFilterCount > 0"
          @reset="clearAllFilters"
        >
          <gaia-popover
            v-for="key in openFilterKeys"
            :key="key"
            placement="bottom-start"
            :show="openChip === key"
            @update:show="(show: boolean) => (openChip = show ? key : null)"
          >
            <template #trigger>
              <gaia-filter-chip
                :field="filterLabel(key)"
                :values="chipValues(key).slice(0, 3)"
                :overflow="
                  chipValues(key).length > 3 ? `+${chipValues(key).length - 3}` : undefined
                "
                :active="openChip === key"
                @toggle="openChip = openChip === key ? null : key"
                @remove="removeFilter(key)"
                @remove-value="(label: string) => removeChipValue(key, label)"
              />
            </template>
            <gaia-filter-value-panel
              :values="filterValues(key)"
              :search="valueSearch[key] ?? ''"
              :picked-label="
                pickedValues(key).length
                  ? `${pickedValues(key).length} ${pickedValues(key).length === 1 ? 'value' : 'values'} selected`
                  : undefined
              "
              @update:search="(term: string) => (valueSearch[key] = term)"
              @toggle="(value: string) => toggleFilterValue(key, value)"
              @clear="clearChipValues(key)"
            />
          </gaia-popover>

          <template #add>
            <gaia-dropdown
              :options="availableFilters.map(filter => ({ key: filter.key, label: filter.label }))"
              placement="bottom-start"
              @select="addFilter"
            >
              <gaia-add-button subject="filter" variant="dashed" />
            </gaia-dropdown>
          </template>
        </gaia-filter-bar>
      </div>

      <div class="grid-area">
        <div v-if="isLoading" class="skeleton-card">
          <gaia-table-skeleton
            :row-style="skeletonRowStyle"
            :cells="['70%', '58%', '62%', '48%', '54%']"
            :rows="8"
            :row-height="48"
            :action="false"
          />
        </div>

        <gaia-empty-state v-else-if="error" status="alert" title="Couldn’t load this view">
          {{ error }}
          <template #actions>
            <gaia-button type="primary" size="small" @click="() => loadViewDetails()">
              Retry
            </gaia-button>
          </template>
        </gaia-empty-state>

        <template v-else-if="viewData">
          <!-- The grid card. ContractViewGrid stays as it is — the cell states,
             frozen name column, resizing and header filters are its own. -->
          <div class="grid-card" data-tour="views.grid" :class="{ refreshing: isRefreshingTable }">
            <ContractViewGrid
              ref="contractViewGridRef"
              :row-data="tableRowData"
              :column-defs="columnDefs"
              :sort-key="sortKey"
              :sort-direction="sortDirection"
              :sort-active="sortActivated"
              @sort="handleSort"
              @add-column="handleCreateColumn"
              @node-expand="handleContractExpand"
              @edit-cell="handleEditCell"
              @go-to-contract="handleGoToContract"
              @go-to-contract-file="handleGoToContractFile"
              @add-revision="handleAddRevision"
              @edit-column-filters="handleEditColumnFilters"
            >
              <template #empty-cell>
                <div class="cell-pending-spinner">
                  <n-spin :size="16" />
                </div>
              </template>
            </ContractViewGrid>

            <!-- Refresh keeps the previous rows readable underneath rather than
               blanking the grid every 15 seconds. -->
            <div v-if="isRefreshingTable && previousTreeNodeData.length > 0" class="refresh-veil">
              <n-flex align="center" :size="8" :wrap="false">
                <n-spin :size="14" />
                <gaia-text size="meta" tone="subtle">Refreshing…</gaia-text>
              </n-flex>
            </div>
          </div>

          <div v-if="totalCount > 0" class="pager">
            <gaia-pagination
              :page="currentPage"
              :page-size="pageSize"
              :total="totalCount"
              @update:page="handlePageChange"
              @update:page-size="handlePageSizeChange"
            />
          </div>
        </template>

        <gaia-empty-state v-else title="No view data available">
          <template #icon>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M2.5 3.5h13v11h-13z" />
              <path d="M2.5 7h13M7 7v7.5" />
            </svg>
          </template>
          This view couldn’t be loaded. Pick a view from the list to display its grid.
        </gaia-empty-state>
      </div>

      <!-- Drawers & modals — unchanged; only the page around them was restyled. -->
      <gaia-view-sources-modal
        v-if="viewData && canEditSources"
        v-model:show="showSourcesModal"
        :view-uid="viewData.uid"
        @saved="handleSourcesSaved"
      />
      <CreateColumnDrawer
        v-if="viewData"
        v-model:show="showCreateColumnDrawer"
        :view-uid="viewData.uid"
        :current-column-count="currentColumnCount"
        @created="handleColumnCreated"
      />
      <UpdateColumnDrawer
        v-if="editingColumnUid"
        v-model:show="showUpdateColumnDrawer"
        :column-uid="editingColumnUid"
        :view-uid="viewData?.uid"
        :filter-options="viewData ? getFilterOptionsForColumn(editingColumnUid) : []"
        :initial-tab="initialDrawerTab"
        @updated="handleColumnUpdated"
        @deleted="handleColumnDeleted"
      />

      <ContractManagementExtractionDetailsModal
        v-model:show="showEditModal"
        :cell-data="editModalCellData"
        :on-data-update="handleEditSave"
      />

      <EditStaticColumnModal
        v-if="staticColumnEditData && staticColumnEditData.staticColumnType"
        v-model:show="showStaticColumnEditModal"
        :static-column-type="staticColumnEditData.staticColumnType"
        :entities="staticColumnEditData.entities"
        :contract-file-uid="staticColumnEditData.contractFileUid"
        :entities-with-files="staticColumnEditData.entitiesWithFiles"
        @removed="handleStaticColumnEntityRemoved"
        @added="handleStaticColumnEntityAdded"
      />

      <EditStaticColumnDrawer
        v-if="viewData && editingStaticColumnType"
        v-model:show="showEditStaticColumnDrawer"
        :static-column-type="editingStaticColumnType"
        :view-data="viewData"
        :entity-name-cache="entityNameCache"
        :initial-tab="initialDrawerTab"
        :type-enum-response-type="typeEnumResponseType"
        :tags-enum-response-type="tagsEnumResponseType"
        @updated="handleStaticColumnUpdated"
      />

      <VaultFileRevisionUploadModal
        v-if="revisionUploadFileUid"
        v-model:show="showRevisionUploadModal"
        :file-uid="revisionUploadFileUid"
        :file-name="revisionUploadFileName"
        @uploaded="loadTableData"
      />
    </div>
  </Layout>
</template>

<style scoped>
/* The grid owns the viewport: chrome is fixed, only the grid scrolls. */
.view-detail {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.title-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: none;
  padding: 12px v-bind(pageX);
  border-bottom: 1px solid v-bind(hairline);
}

/* The strip is page chrome, not part of the grid card, so it spans the full
   width on its own tinted band. */
.filter-strip {
  flex: none;
  padding: 9px v-bind(pageX);
  border-bottom: 1px solid v-bind(hairline);
  background: v-bind(stripSurface);
}

.grid-area {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px v-bind(pageX) 20px;
  gap: 12px;
}

.skeleton-card {
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  background: v-bind(surface);
  overflow: hidden;
}

.grid-card {
  position: relative;
  flex: 1;
  min-height: 0;
  border: 1px solid v-bind(cardBorder);
  border-radius: 12px;
  background: v-bind(surface);
  overflow: hidden;
}

.grid-card.refreshing {
  /* Nothing moves — the veil below carries the state so rows stay readable. */
  cursor: progress;
}

/* Bottom-right, out of the way of the frozen name column. */
.refresh-veil {
  position: absolute;
  right: 14px;
  bottom: 14px;
  padding: 7px 11px;
  border-radius: 9px;
  border: 1px solid v-bind(cardBorder);
  background: v-bind(scrim);
  box-shadow: 0 8px 24px rgba(25, 27, 31, 0.12);
  z-index: 3;
}

.pager {
  flex: none;
}

.cell-pending-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
